import { ListItem } from "../models/base/ListItem";

const swap = <T>(array: T[], ai: number, bi: number): T[] => {
  //
  const a = array[ai];
  const b = array[bi];
  // beforeA - all elements before a
  const beforeA = array.slice(0, ai);
  // afterB - all elements after b
  const afterB = array.slice(bi + 1);
  // compose new array, note a and b are swapped
  return [...beforeA, b, a, ...afterB];
};

const prev = <T extends ListItem>(currentItem: T, array: T[]): T | undefined => {
  // get the index of the item
  const currentItemIndex = array.findIndex((item) => item.id === currentItem.id);
  // cannot find current item in array
  if (currentItemIndex < 0) return;
  // check if it's the first, then return undef
  if (currentItemIndex <= 0) return;
  // get next item and return
  return array[currentItemIndex - 1];
};

const next = <T extends ListItem>(currentItem: T, array: T[]): T | undefined => {
  // get the index of the item
  const currentItemIndex = array.findIndex((item) => item.id === currentItem.id);
  // cannot find current item in array
  if (currentItemIndex < 0) return;
  // check if it's the last, then return undef
  if (currentItemIndex >= array.length - 1) return;
  // get next item and return
  return array[currentItemIndex + 1];
};

const moveUp = <T>(array: T[], i: number): T[] => {
  const min = 1;
  const max = array.length - 1;
  if (i < min || i > max) return array;

  // ai - index of element to be moved down
  const ai = i - 1;
  // bi - index of element to be moved up
  const bi = i;

  return swap(array, ai, bi);
};

const moveDown = <T>(array: T[], i: number): T[] => {
  //
  const min = 0;
  const max = array.length - 2;

  if (i < min || i > max) return array;

  // already last element
  if (i >= array.length - 1) return array;

  // ai - index of element to be moved down
  const ai = i;
  // bi - index of the element to be moved up
  const bi = i + 1;

  return swap(array, ai, bi);
};

const remove = <T>(array: T[], index: number) => {
  return array.filter((_, i) => i !== index);
};

const replace = <T>(array: T[], index: number, value: T) => {
  return [...array.slice(0, index), value, ...array.slice(index + 1)];
};

const sortByNameFn = (a: { name: string }, b: { name: string }) => {
  var nameA = a.name.toUpperCase(); // ignore upper and lowercase
  var nameB = b.name.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }

  // names must be equal
  return 0;
};

const sortBySortOrderFn = (a: { sortOrder: number }, b: { sortOrder: number }) => {
  if (a.sortOrder < b.sortOrder) {
    return -1;
  }
  if (a.sortOrder > b.sortOrder) {
    return 1;
  }

  // names must be equal
  return 0;
};

const sortByName = <T extends { name: string }>(list: T[]) => {
  const shallowCopy = [...list];
  return shallowCopy.sort(sortByNameFn);
};

const sortBySortOrder = <T extends { sortOrder: number }>(list: T[]) => {
  const shallowCopy = [...list];
  return shallowCopy.sort(sortBySortOrderFn);
};

export const arrayUtils = {
  prev,
  next,
  moveUp,
  moveDown,
  replace,
  remove,
  sortByName,
  sortBySortOrder,
};
